import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Trophy, Plus, Edit2, Trash2, Copy, ExternalLink, Loader2, Search, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import TournamentHubForm from '../components/tournament-hub/TournamentHubForm';
import { AutocompleteInput } from '../components/tournament-hub/AutocompleteInput';
import { SPORTS, TOURNAMENT_TYPES, STATUSES, STATUS_STYLES, DATE_PRESETS, emptyTournament, formatDate } from '../components/tournament-hub/tournamentHubUtils';
import { API } from '../utils/apiConfig';

const SortableHead = ({ label, sortKey, sortConfig, onSort, className = '' }) => {
  const active = sortConfig.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sortConfig.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-slate-900 select-none"
        data-testid={`sort-${sortKey}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? 'text-amber-600' : 'text-slate-400'}`} />
      </button>
    </TableHead>
  );
};

export default function TournamentHubAdminPage() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyTournament());
  const [saving, setSaving] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'tournament_date', dir: 'asc' });

  const [filters, setFilters] = useState({ search: '', sport: 'all', tournament_type: 'all', venue: '', date_preset: '', date_from: '', date_to: '' });

  const handleSort = (key) => {
    setSortConfig((c) => ({ key, dir: c.key === key && c.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortConfig.key, sort_dir: sortConfig.dir };
      if (filters.search) params.search = filters.search;
      if (filters.sport !== 'all') params.sport = filters.sport;
      if (filters.tournament_type !== 'all') params.tournament_type = filters.tournament_type;
      if (filters.venue) params.venue = filters.venue;
      if (filters.date_preset && filters.date_preset !== 'custom') params.date_preset = filters.date_preset;
      if (filters.date_preset === 'custom') {
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      }
      const res = await axios.get(`${API}/tournament-hub`, { params });
      setTournaments(res.data.tournaments || []);
    } catch (e) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig]);

  useEffect(() => { const t = setTimeout(fetchList, 250); return () => clearTimeout(t); }, [fetchList]);

  const openCreate = () => { setEditing(null); setFormData(emptyTournament()); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setFormData({ ...emptyTournament(), ...t, prize_pool: t.prize_pool || '' }); setModalOpen(true); };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.tournament_date) {
      toast.error('Tournament Name and Date are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, prize_pool: formData.prize_pool ? parseFloat(formData.prize_pool) : 0 };
      if (editing) {
        await axios.put(`${API}/tournament-hub/${editing.id}`, payload);
        toast.success('Tournament updated');
      } else {
        await axios.post(`${API}/tournament-hub`, payload, { params: { force: true } });
        toast.success('Tournament created');
      }
      setModalOpen(false);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tournament');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/tournament-hub/${t.id}`);
      toast.success('Tournament deleted');
      fetchList();
    } catch (e) {
      toast.error('Failed to delete tournament');
    }
  };

  const handleDuplicate = async (t) => {
    try {
      await axios.post(`${API}/tournament-hub/${t.id}/duplicate`);
      toast.success('Tournament duplicated');
      fetchList();
    } catch (e) {
      toast.error('Failed to duplicate tournament');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tournament-hub-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight flex items-center gap-3">
              <Trophy className="w-8 h-8 text-amber-500" /> Tournaments Hub
            </h1>
            <p className="text-slate-600 mt-1">Manage tournament listings submitted by organisers</p>
          </div>
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600" data-testid="create-tournament-hub-btn">
            <Plus className="w-4 h-4 mr-2" /> New Tournament
          </Button>
        </div>

        <Card className="p-4 border-slate-200">
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="relative lg:col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search by tournament, organiser or organisation name" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="admin-search-input" />
            </div>
            <Select value={filters.sport} onValueChange={(v) => setFilters((f) => ({ ...f, sport: v }))}>
              <SelectTrigger data-testid="admin-sport-filter"><SelectValue placeholder="Sport" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Sports</SelectItem>{SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.tournament_type} onValueChange={(v) => setFilters((f) => ({ ...f, tournament_type: v }))}>
              <SelectTrigger data-testid="admin-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Types</SelectItem>{TOURNAMENT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <AutocompleteInput field="venue" value={filters.venue} onChange={(v) => setFilters((f) => ({ ...f, venue: v }))} placeholder="Filter by venue" testId="admin-venue-filter" />
            <Select value={filters.date_preset || 'none'} onValueChange={(v) => setFilters((f) => ({ ...f, date_preset: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="admin-date-preset-filter"><SelectValue placeholder="Date Range" /></SelectTrigger>
              <SelectContent>{DATE_PRESETS.map((d) => <SelectItem key={d.value || 'none'} value={d.value || 'none'}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {filters.date_preset === 'custom' && (
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="admin-date-from" />
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="admin-date-to" />
            </div>
          )}
        </Card>

        <Card className="border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-12 text-slate-400" data-testid="admin-empty-state">No tournaments found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Sport / Type</TableHead>
                  <SortableHead label="Date" sortKey="tournament_date" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Venue" sortKey="venue" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Organiser" sortKey="organiser_name" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Organiser Number" sortKey="mobile_number" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Prize Pool" sortKey="prize_pool" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tournaments.map((t) => (
                  <TableRow key={t.id} data-testid={`admin-tournament-row-${t.id}`}>
                    <TableCell className="font-medium text-slate-900">{t.name}</TableCell>
                    <TableCell className="text-slate-500">{t.sport} · {t.tournament_type}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(t.tournament_date)}</TableCell>
                    <TableCell className="text-slate-500">{t.venue || '-'}</TableCell>
                    <TableCell className="text-slate-500">{t.organiser_name || '-'}</TableCell>
                    <TableCell className="text-slate-500">{t.mobile_number || '-'}</TableCell>
                    <TableCell className="text-slate-500">{t.prize_pool ? `₹${Number(t.prize_pool).toLocaleString('en-IN')}` : '-'}</TableCell>
                    <TableCell><span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${STATUS_STYLES[t.status]}`}>{t.status}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => window.open(`/hub/${t.id}`, '_blank')} data-testid={`view-btn-${t.id}`}><ExternalLink className="w-4 h-4 text-slate-500" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-btn-${t.id}`}><Edit2 className="w-4 h-4 text-blue-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDuplicate(t)} data-testid={`duplicate-btn-${t.id}`}><Copy className="w-4 h-4 text-slate-500" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t)} data-testid={`delete-btn-${t.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tournament-hub-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />{editing ? 'Edit Tournament' : 'Create Tournament'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editing && (
              <div className="space-y-1.5">
                <label className="text-sm text-slate-600">Status</label>
                <Select value={formData.status} onValueChange={(v) => setFormData((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <TournamentHubForm formData={formData} setFormData={setFormData} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600" data-testid="save-tournament-hub-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
